import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { getImageUrl } from "../../Utils/helpers";
import * as C from "./styled";
import ChatUser, {
  getChannelLatestTime,
  getChannelRequiresReply,
} from "../../Components/Chat/ChatUser";
import Messages from "../../Components/Chat/Messages";
import { MdVerified } from "react-icons/md";
import { GoPaperAirplane } from "react-icons/go";
import { FaArrowLeft } from "react-icons/fa6";
import { getUserDetails } from "../../Redux/slices/userSlice";
import { AppDispatch, RootState } from "../../Redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  appendNewMessage,
  togglePaymentModal,
  sendMessage,
  fetchChannels,
  fetchChannelMessages,
  selectChannel,
  markMessagesAsRead,
} from "../../Redux/slices/messageSlice";
import {
  FANXOBILLPHONENUMBER,
  getSessionToken,
  isCreatorRole,
  loadScript,
} from "../../Config/common.config";
import { io, Socket } from "socket.io-client";
import { Message } from "../../Config/interface.config";
import CommonLoader from "../../Components/CommonLoader";
import ChatPaymentModal from "./ChatPaymentModal";
import APIRequest from "../../API";
import * as PATH from "../../API/path";
import toast from "react-hot-toast";
import { OrderResponse } from "./chat.interface";
import { fetchMemberShipDetails } from "../../Redux/slices/settingSlice";
import userImg from "../../Assets/Images/user-profile-img.webp";
import { Capitalize } from "../../Utils";
import { isMobile } from "react-device-detect";
import EmptyState from "../../Components/Chat/EmptyState";
import GiftButton from "./GiftButton";

const socketURL =
  process.env.REACT_APP_SOCKET_ENDPOINT ||
  process.env.REACT_SOCKET_ENDPOINT ||
  "http://localhost:3000";

interface CreatorPresenceEvent {
  creatorId: string;
  isOnline: boolean;
}

interface CreatorPresenceResponse {
  statuses?: Record<string, boolean>;
}

const Chat: React.FC = () => {
  const [showUserList, setShowUserList] = useState<boolean>(isMobile);
  const [newMessage, setNewMessage] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [creatorPresence, setCreatorPresence] = useState<
    Record<string, boolean>
  >({});

  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.user);
  const { activeChat, channels, messagesByChannel, channelsLoading } =
    useSelector((state: RootState) => state.message);

  const selectedChannel = channels.find(
    (channel: any) => channel.channelSlug === activeChat
  );

  const messageState = messagesByChannel[activeChat] || {
    messages: [],
    loading: false,
    error: null,
    page: 1,
    hasMore: true,
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const directOpenHandledRef = useRef<string>("");

  const loggedInUserIsCreator = isCreatorRole(user.role);
  const canViewCreatorPresence = !loggedInUserIsCreator;
  const recipientIsCreator =
    selectedChannel?.user?.role?.role === "creator";
  const selectedCreatorId = recipientIsCreator
    ? selectedChannel?.user?._id || ""
    : "";
  const selectedCreatorIsOnline = Boolean(
    selectedCreatorId && creatorPresence[selectedCreatorId]
  );

  const channelSlugsKey = useMemo(
    () =>
      channels
        .map((channel: any) => String(channel?.channelSlug || ""))
        .filter(Boolean)
        .sort()
        .join("|"),
    [channels]
  );

  const creatorIdsKey = useMemo(() => {
    if (!canViewCreatorPresence) return "";

    return Array.from(
      new Set(
        channels
          .filter(
            (channel: any) => channel?.user?.role?.role === "creator"
          )
          .map((channel: any) => String(channel?.user?._id || ""))
          .filter(Boolean)
      )
    )
      .sort()
      .join("|");
  }, [channels, canViewCreatorPresence]);

  const canSendGift =
    Boolean(selectedChannel?.user?._id && activeChat) &&
    !loggedInUserIsCreator &&
    recipientIsCreator;

  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  useEffect(() => {
    if (user.id) dispatch(fetchChannels());
  }, [user.id, dispatch]);

  useEffect(() => {
    if (selectedChannel && recipientIsCreator) {
      dispatch(fetchMemberShipDetails(selectedChannel?.user?._id));
    }

    if (selectedChannel && activeChat && !messagesByChannel[activeChat]) {
      dispatch(
        fetchChannelMessages({ channelSlug: activeChat, page: 1, limit: 20 })
      );
    }
  }, [
    selectedChannel,
    recipientIsCreator,
    dispatch,
    activeChat,
    messagesByChannel,
  ]);

  useEffect(() => {
    const state = location.state as
      | { channelSlug?: string; userId?: string; openChat?: boolean }
      | null;
    const targetChannelSlug = state?.channelSlug;
    const targetUserId = state?.userId;
    const targetKey = targetChannelSlug || targetUserId;

    if (!state?.openChat || !targetKey || !channels.length) return;
    if (directOpenHandledRef.current === targetKey) return;

    const channel = targetChannelSlug
      ? channels.find((item: any) => item.channelSlug === targetChannelSlug)
      : channels.find(
          (item: any) =>
            item.user?._id === targetUserId || item.user?.id === targetUserId
        );

    if (!channel?.channelSlug) return;

    directOpenHandledRef.current = targetKey;
    dispatch(selectChannel({ channelSlug: channel.channelSlug }));
    dispatch(markMessagesAsRead({ channelSlug: channel.channelSlug }));
    dispatch(
      fetchChannelMessages({
        channelSlug: channel.channelSlug,
        page: 1,
        limit: 20,
      })
    );

    if (isMobile) setShowUserList(false);
  }, [channels, dispatch, location.state]);

  const sendMessageService = useCallback(async () => {
    if (isSending) return;
    setIsSending(true);

    try {
      const messagePayload = {
        text: newMessage,
        from: user.id ?? "",
        to: selectedChannel?.user?._id ?? "",
        channelSlug: activeChat,
        createdAt: new Date().toISOString(),
      };
      const result = await dispatch(sendMessage(messagePayload as any)).unwrap();

      if (result?._id) {
        socketRef.current?.emit("new-message", {
          message: result,
          roomId: selectedChannel?.channelSlug,
        });
        setNewMessage("");
      }
    } catch (err) {
      console.error("Failed to send message", err);
    } finally {
      setIsSending(false);
    }
  }, [newMessage, user.id, selectedChannel, activeChat, dispatch, isSending]);

  const handleNewMessage = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!newMessage.trim()) return;

      const recipient = selectedChannel?.user;
      const recipientHasMessagePrice = recipient?.memberDetails?.messagePrice;
      const senderIsNotCreator = !isCreatorRole(user.role);

      if (senderIsNotCreator && recipientHasMessagePrice) {
        dispatch(togglePaymentModal());
        return;
      }

      sendMessageService();
    },
    [newMessage, user.role, dispatch, sendMessageService, selectedChannel]
  );

  useEffect(() => {
    if (!user.id || !channelSlugsKey) return;

    const channelSlugs = channelSlugsKey.split("|").filter(Boolean);
    const creatorIds = creatorIdsKey.split("|").filter(Boolean);
    const visibleCreatorIds = new Set(creatorIds);
    const socket = io(socketURL, {
      auth: { token: getSessionToken() },
      reconnection: true,
    });

    socketRef.current = socket;

    const subscribeToChannelsAndPresence = () => {
      channelSlugs.forEach((channelSlug) => {
        socket.emit("join", channelSlug);
      });

      if (canViewCreatorPresence && creatorIds.length > 0) {
        socket.emit(
          "creator-presence:subscribe",
          { creatorIds },
          (response: CreatorPresenceResponse) => {
            setCreatorPresence(response?.statuses || {});
          }
        );
      }
    };

    const handleNewSocketMessage = ({ message }: { message: Message }) => {
      dispatch(appendNewMessage({ newmessage: message }));
    };

    const handleCreatorPresence = ({
      creatorId,
      isOnline,
    }: CreatorPresenceEvent) => {
      if (!visibleCreatorIds.has(creatorId)) return;

      setCreatorPresence((currentPresence) => ({
        ...currentPresence,
        [creatorId]: Boolean(isOnline),
      }));
    };

    const handleSocketDisconnect = () => {
      if (canViewCreatorPresence) {
        setCreatorPresence({});
      }
    };

    socket.on("connect", subscribeToChannelsAndPresence);
    socket.on("new-message", handleNewSocketMessage);
    socket.on("creator-presence", handleCreatorPresence);
    socket.on("disconnect", handleSocketDisconnect);

    return () => {
      if (creatorIds.length > 0) {
        socket.emit("creator-presence:unsubscribe", { creatorIds });
      }
      socket.off("connect", subscribeToChannelsAndPresence);
      socket.off("new-message", handleNewSocketMessage);
      socket.off("creator-presence", handleCreatorPresence);
      socket.off("disconnect", handleSocketDisconnect);
      socket.disconnect();

      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [
    user.id,
    channelSlugsKey,
    creatorIdsKey,
    canViewCreatorPresence,
    dispatch,
  ]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messageState.messages.length]);

  const initiatePayment = async ({ data }: OrderResponse) => {
    dispatch(togglePaymentModal());
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const paymentObject = new (window as any).Razorpay({
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: data.order.amount.toString(),
      currency: "INR",
      name: "Fanxo messages",
      description: "User paid to chat with the connection",
      order_id: data.order.order_id,
      handler: function () {
        // Razorpay confirms the payment in the browser, but delivery is owned
        // by the server webhook so a mobile callback/network interruption
        // cannot leave a paid message unsent.
        setNewMessage("");
        toast.success("Payment confirmed. Your message is being sent.");
        window.setTimeout(() => {
          if (activeChat) {
            dispatch(fetchChannelMessages({ channelSlug: activeChat, page: 1, limit: 20 }));
          }
        }, 1500);
      },
      prefill: {
        name: "Fanxo",
        email: "fanxobusiness@gmail.com",
        contact: FANXOBILLPHONENUMBER,
      },
      theme: { color: "#001676" },
    });

    paymentObject.open();
  };

  const handleCreateNewOrder = async () => {
    try {
      const response = await APIRequest(PATH.createOrder, {
        creatorId: !isCreatorRole(user.role)
          ? selectedChannel?.user?._id
          : user.id,
        orderType: "message",
        message: newMessage,
        channelSlug: activeChat,
      });

      if (response.error) {
        return toast.error(
          response.error.message ||
            "Error in initiating payment, please try again later"
        );
      }

      if (response.data?.status === false) {
        const backendError =
          response.data?.data?.error?.description ||
          response.data?.data?.message ||
          "Error in initiating payment, please try again later";
        return toast.error(backendError);
      }

      toast.success(response.data?.message || "Order placed successfully");
      initiatePayment(response.data as any);
    } catch (err) {
      console.error("handleCreateNewOrder error:", err);
      toast.error("Error in initiating payment, please try again later");
    }
  };

  const handleGiftSent = (message: any) => {
    dispatch(appendNewMessage({ newmessage: message }));
    socketRef.current?.emit("new-message", {
      message,
      roomId: selectedChannel?.channelSlug,
    });
  };

  const onSelectChannel = (channelSlug: string) => {
    dispatch(selectChannel({ channelSlug }));
    dispatch(markMessagesAsRead({ channelSlug }));

    if (!messagesByChannel[channelSlug]) {
      dispatch(fetchChannelMessages({ channelSlug, page: 1, limit: 20 }));
    }

    if (isMobile) {
      setShowUserList(false);
    } else {
      setShowUserList(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
    const charCount = text.length;

    if (wordCount <= 350 && charCount <= 2000) {
      setNewMessage(text);
    }
  };

  useEffect(() => {
    if (!isMobile) setShowUserList(true);
  }, []);

  return (
    <C.MessageWrapper>
      {selectedChannel?.user?.memberDetails?.messagePrice && (
        <ChatPaymentModal
          onSend={handleCreateNewOrder}
          messageCharge={selectedChannel.user.memberDetails.messagePrice.toString()}
        />
      )}

      <C.MessageShader
        isVisible={showUserList}
        isMobile={isMobile}
        onClick={() => setShowUserList(!showUserList)}
      />

      <C.MessageHeader>
        <C.MessageHeading className="text-white font-bold text-3xl">
          {isMobile && !showUserList && (
            <FaArrowLeft
              className="menu-icon"
              onClick={() => setShowUserList(!showUserList)}
            />
          )}
          <span>Messages</span>
        </C.MessageHeading>
      </C.MessageHeader>

      <C.MessageContainer>
        {channelsLoading ? (
          <CommonLoader message="Loading channels..." />
        ) : channels.length === 0 ? (
          isMobile ? (
            <C.MobileEmptyStateWrapper>
              <EmptyState />
            </C.MobileEmptyStateWrapper>
          ) : (
            <C.EmptyMessageMainPanel>
              <EmptyState />
            </C.EmptyMessageMainPanel>
          )
        ) : (
          <>
            {(!isMobile || showUserList) && (
              <C.UserListWrallper showUserList={showUserList}>
                {channels
                  .slice()
                  .sort((a, b) => {
                    const aPending = getChannelRequiresReply(a, user.id ?? "");
                    const bPending = getChannelRequiresReply(b, user.id ?? "");
                    if (aPending !== bPending) return aPending ? -1 : 1;
                    return getChannelLatestTime(b) - getChannelLatestTime(a);
                  })
                  .map((channel: any) => {
                    const channelUserId = String(channel?.user?._id || "");
                    const channelUserIsCreator =
                      channel?.user?.role?.role === "creator";
                    const showPresence =
                      canViewCreatorPresence && channelUserIsCreator;

                    return (
                      <ChatUser
                        channeluser={channel.user}
                        channel={channel}
                        userId={user.id ?? ""}
                        key={`user-${channelUserId}`}
                        showCreatorPresence={showPresence}
                        isCreatorOnline={Boolean(
                          channelUserId && creatorPresence[channelUserId]
                        )}
                        onSelectUser={() => {
                          onSelectChannel(channel?.channelSlug);
                          if (isMobile) setShowUserList(false);
                        }}
                      />
                    );
                  })}
              </C.UserListWrallper>
            )}

            {selectedChannel && (!isMobile || !showUserList) && (
              <C.MessageSection>
                <C.SeletedChatUser>
                  <C.UserImage
                    alt="user"
                    src={
                      getImageUrl(selectedChannel?.user?.image?.fileURL ?? "") ||
                      userImg
                    }
                    onError={(event) => {
                      if (event.currentTarget.src !== userImg) {
                        event.currentTarget.src = userImg;
                      }
                    }}
                  />
                  <C.SelectedChatIdentity>
                    <C.SelectedChatName>
                      {Capitalize(selectedChannel.user.fullname)}
                      <span>{recipientIsCreator && <MdVerified />}</span>
                    </C.SelectedChatName>
                    {canViewCreatorPresence && recipientIsCreator && selectedCreatorIsOnline && (
                      <C.CreatorPresenceStatus $online>
                        <span aria-hidden="true" />
                        Online
                      </C.CreatorPresenceStatus>
                    )}
                  </C.SelectedChatIdentity>
                </C.SeletedChatUser>

                {user.id && activeChat && (
                  <C.MessagesWrapper id="message-container">
                    <Messages
                      key={activeChat}
                      channelSlug={activeChat}
                      messages={messageState.messages}
                      hasMore={messageState.hasMore}
                      loading={messageState.loading}
                      loadMore={() => {
                        if (messageState.hasMore && !messageState.loading) {
                          dispatch(
                            fetchChannelMessages({
                              channelSlug: activeChat,
                              page: (messageState.page || 1) + 1,
                              limit: 20,
                            })
                          );
                        }
                      }}
                      messagesEndRef={messagesEndRef}
                    />
                  </C.MessagesWrapper>
                )}

                <C.MessageActionWrapper
                  onSubmit={handleNewMessage}
                  isMobile={isMobile}
                >
                  <GiftButton
                    creatorId={selectedChannel?.user?._id}
                    creatorName={selectedChannel?.user?.fullname}
                    channelSlug={activeChat}
                    canSendGift={canSendGift}
                    onGiftSent={handleGiftSent}
                  />

                  <C.NewMessage
                    type="text"
                    value={newMessage}
                    placeholder="Write a message..."
                    onChange={handleInputChange}
                  />

                  <C.MessageBtnWrapper>
                    <C.MessageActionBtn
                      type="submit"
                      color="#ffffff"
                      bgcolor="transparent"
                    >
                      <GoPaperAirplane
                        size={24}
                        color="#000"
                        className="button-icon"
                      />
                    </C.MessageActionBtn>
                  </C.MessageBtnWrapper>

                  <div className="text-sm text-gray-400 mt-1 text-right">
                    {newMessage.trim().split(/\s+/).filter(Boolean).length}/350
                  </div>
                </C.MessageActionWrapper>
              </C.MessageSection>
            )}
          </>
        )}
      </C.MessageContainer>
    </C.MessageWrapper>
  );
};

export default Chat;
